import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * Scan the canvas for the bounding box of non-transparent pixels and return
 * a cropped PNG data URL. Used so the exported signature image is tight to
 * the ink — embedding the full pad canvas means the signature occupies only
 * a small fraction of the PDF field rect after `pdf-lib`'s aspect-preserving
 * scale, making it look faint.
 */
const trimCanvasToInkedBbox = (canvas: HTMLCanvasElement): string => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.toDataURL('image/png');
  const w = canvas.width;
  const h = canvas.height;
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 0) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return canvas.toDataURL('image/png');
  // Small margin so stroke ends aren't clipped flush against the edge.
  const pad = Math.round(Math.min(w, h) * 0.02);
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(w - 1, maxX + pad);
  maxY = Math.min(h - 1, maxY + pad);
  const cw = maxX - minX + 1;
  const ch = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = cw;
  out.height = ch;
  const octx = out.getContext('2d');
  if (!octx) return canvas.toDataURL('image/png');
  octx.drawImage(canvas, minX, minY, cw, ch, 0, 0, cw, ch);
  return out.toDataURL('image/png');
};

/**
 * Imperative handle exposed to the parent so the PDF-survey form can query
 * the signature on submit without forcing a re-render on every stroke.
 */
export interface SignaturePadHandle {
  isEmpty: () => boolean;
  /** Returns a PNG data URL or `null` when the pad is empty. */
  toDataUrl: () => string | null;
  clear: () => void;
}

interface SignaturePadFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  /**
   * Fires when the user finishes a stroke or clears the pad, useful for the
   * preview pane that re-bakes the PDF in response to changes.
   */
  onChange?: (hasContent: boolean) => void;
}

/**
 * Minimal canvas-based signature pad. Loads `signature_pad` lazily so the
 * lib only enters the JS bundle when a candidate actually reaches a
 * `pdf_survey` step.
 */
export const SignaturePadField = forwardRef<SignaturePadHandle, SignaturePadFieldProps>(
  ({ label, required, error, onChange }, ref) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const padRef = useRef<any>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const [empty, setEmpty] = useState(true);
    const [ready, setReady] = useState(false);

    useEffect(() => {
      let cancelled = false;
      let pad: any = null;
      let resizeHandler: (() => void) | null = null;

      const init = async () => {
        const mod = await import('signature_pad');
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const resize = () => {
          // Match canvas backing store to its CSS box * dpr for crisp lines.
          const ratio = Math.max(window.devicePixelRatio || 1, 1);
          canvas.width = canvas.offsetWidth * ratio;
          canvas.height = canvas.offsetHeight * ratio;
          canvas.getContext('2d')?.scale(ratio, ratio);
          if (pad) pad.clear();
          setEmpty(true);
          onChangeRef.current?.(false);
        };
        resize();
        resizeHandler = resize;
        window.addEventListener('resize', resize);

        const SignaturePad = (mod as any).default ?? (mod as any).SignaturePad;
        pad = new SignaturePad(canvas, {
          penColor: '#0a2540',
          // Thicker strokes so the signature reads clearly after pdf-lib
          // scales the exported PNG down into the (typically small) PDF
          // field rect. Earlier values (0.8 / 2.2) rendered as a hairline.
          minWidth: 1.4,
          maxWidth: 3.6,
        });
        pad.addEventListener('beginStroke', () => {
          setEmpty(false);
        });
        pad.addEventListener('endStroke', () => {
          onChangeRef.current?.(true);
        });
        padRef.current = pad;
        setReady(true);
      };
      init();

      return () => {
        cancelled = true;
        if (resizeHandler) window.removeEventListener('resize', resizeHandler);
        padRef.current = null;
      };
    }, []);

    useImperativeHandle(ref, () => ({
      isEmpty: () => !padRef.current || padRef.current.isEmpty(),
      toDataUrl: () => {
        if (!padRef.current || padRef.current.isEmpty()) return null;
        const canvas = canvasRef.current;
        if (!canvas) return padRef.current.toDataURL('image/png');
        // Crop the canvas to the inked bbox so the signature fills the PDF
        // rect after pdf-lib scales it. The pad canvas is wider than most
        // signatures, so without trimming, the actual ink ends up as a
        // small fraction of the rect and looks faint. Falls back to the
        // full canvas if the trim can't read pixels (e.g. tainted canvas).
        try {
          return trimCanvasToInkedBbox(canvas);
        } catch {
          return padRef.current.toDataURL('image/png');
        }
      },
      clear: () => {
        padRef.current?.clear();
        setEmpty(true);
        onChangeRef.current?.(false);
      },
    }));

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-rgf-gray-700">
            {label} {required && <span className="text-red-600">*</span>}
          </label>
          <button
            type="button"
            className="text-xs text-rgf-gray-500 hover:text-rgf-gray-800 underline"
            onClick={() => {
              padRef.current?.clear();
              setEmpty(true);
              onChangeRef.current?.(false);
            }}
            disabled={!ready}
          >
            Clear
          </button>
        </div>
        <div
          className={`relative rounded-md border ${
            error ? 'border-red-300' : 'border-dashed border-rgf-gray-300'
          } bg-rgf-gray-50 overflow-hidden`}
        >
          <canvas
            ref={canvasRef}
            className="block w-full"
            style={{ height: 140, touchAction: 'none', cursor: 'crosshair' }}
            aria-label={`${label} signature pad`}
          />
          {empty && ready && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-rgf-gray-400 italic">
              Draw your signature here
            </div>
          )}
          {!ready && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-rgf-gray-400">
              Loading signature pad…
            </div>
          )}
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  },
);
SignaturePadField.displayName = 'SignaturePadField';
